import { t, type Locale } from '@sd/i18n';
import type { GameState } from '@sd/sim';
import type { Viewer } from '../map/describe';
import { escapeHtml, num, seatColor } from './format';
import { icon } from './icons';
import { playerRows, raceRows } from './standing';

/**
 * The left column (#56, docs/design/ui-style.md): the race for victory and
 * the players. It folds into a rail of two icons by the handle on its edge
 * or the `[` key; a red dot on an icon means a threat inside.
 */
export class LeftColumn {
  private readonly el: HTMLElement;
  /** The fold handle stays put; only the content below it is redrawn. */
  private readonly handleEl: HTMLButtonElement;
  private readonly content: HTMLElement;
  private open = true;
  private last = '';

  constructor(
    parent: HTMLElement,
    private readonly viewer: Viewer,
    private readonly onFold: (open: boolean) => void,
  ) {
    this.el = document.createElement('aside');
    this.el.className = 'sd-col';
    this.handleEl = document.createElement('button');
    this.handleEl.type = 'button';
    this.handleEl.className = 'sd-toggle sd-toggle--left';
    this.handleEl.dataset.fold = '';
    this.content = document.createElement('div');
    this.content.className = 'sd-col__content';
    this.el.append(this.handleEl, this.content);
    parent.append(this.el);
    this.drawHandle();
    this.el.addEventListener('click', (event) => {
      if ((event.target as HTMLElement).closest('[data-fold]')) this.toggle();
    });
  }

  toggle() {
    this.open = !this.open;
    this.last = '';
    this.drawHandle();
    this.onFold(this.open);
  }

  private drawHandle() {
    const { locale } = this.viewer;
    this.handleEl.setAttribute('aria-expanded', String(this.open));
    this.handleEl.title = t(locale, this.open ? 'ui.panel.fold' : 'ui.panel.unfold');
    this.handleEl.innerHTML = icon(this.open ? 'left' : 'right', 16);
  }

  get isOpen(): boolean {
    return this.open;
  }

  update(state: GameState) {
    const { locale } = this.viewer;
    const races = raceRows(state, this.viewer);
    const players = playerRows(state, this.viewer);
    const threat =
      races.some((r) => r.alert) || players.some((p) => p.relations.some((r) => r.alert));
    const html = this.open ? this.render(locale, races, players) : this.rail(locale, threat);
    if (html === this.last) return;
    this.last = html;
    this.el.dataset.open = String(this.open);
    this.content.innerHTML = html;
  }

  private rail(locale: Locale, threat: boolean): string {
    return `
      <nav class="sd-panel sd-rail">
        <button class="sd-rail__btn" type="button" data-fold title="${t(locale, 'race.title')}">${icon('race')}${
          threat ? '<span class="sd-rail__dot sd-rail__dot--alert"></span>' : ''
        }</button>
        <button class="sd-rail__btn" type="button" data-fold title="${t(locale, 'players.title')}">${icon('players')}</button>
      </nav>`;
  }

  private render(
    locale: Locale,
    races: ReturnType<typeof raceRows>,
    players: ReturnType<typeof playerRows>,
  ): string {
    const bars = (row: (typeof races)[number]) =>
      row.progress.length
        ? `<div class="sd-race__bars">${row.progress
            .map(
              (p) =>
                // The goal line stands at 70% of the track, so the leader can be seen past it.
                `<div class="sd-race__bar"><span style="--seat:${seatColor(p.playerId)};width:${Math.min(100, p.value * 70)}%"></span></div>`,
            )
            .join('')}<span class="sd-race__goal" style="left:70%"></span></div>`
        : '';
    return `
      <section class="sd-panel">
        <h2>${t(locale, 'race.title')}</h2>
        <ol class="sd-race">
          ${races
            .map(
              (r) => `<li>
                <div class="sd-race__head"><h3>${escapeHtml(r.title)}</h3><span class="sd-race__status">${escapeHtml(r.status)}</span></div>
                ${bars(r)}
                <div class="sd-race__note ${r.alert ? 'sd-race__note--alert' : ''}">${escapeHtml(r.note)}</div>
              </li>`,
            )
            .join('')}
        </ol>
      </section>
      <section class="sd-panel">
        <div class="sd-section__head"><h2 style="margin:0">${t(locale, 'players.title')}</h2><span class="sd-label">${t(locale, 'players.columns')}</span></div>
        <ul class="sd-players">
          ${players
            .map(
              (p) => `<li>
                <span class="sd-emblem sd-emblem--sm" style="--seat:${seatColor(p.id)}"></span>
                <span class="sd-players__name">${escapeHtml(p.name)} <small>${t(locale, `faction.${p.faction}.name`)}</small></span>
                <span>${num(locale, p.prestige, 1)}</span>
                <span class="sd-muted">${num(locale, p.rate, 2)}</span>
                ${
                  p.relations.length
                    ? `<span class="sd-players__relations">${p.relations
                        .map(
                          (r) =>
                            `<span class="sd-badge ${r.alert ? 'sd-badge--alert' : ''}">${escapeHtml(r.text)}</span>`,
                        )
                        .join('')}</span>`
                    : ''
                }
              </li>`,
            )
            .join('')}
        </ul>
      </section>`;
  }

  dispose() {
    this.el.remove();
  }
}
